import * as React from "react";
import {Theme} from "@vebox/etui-themes";
import {accordionStyle} from "../../pages/CensusStatusPage";
import {useTheme} from "@vebox/etui-system";
import {SuggestedActionsTable} from "../SuggestedActionsTable";
import {DesAccordion, DesAccordionSection,} from "../../../../../components/DesAccordion";
import {renderHeader} from "./utils";
import {
    Actions,
    ActionsEnum,
    CensusEquipmentStatusDetails,
    CensusEquipmentStatusDetailsEnum,
    CensusEquipmentStatuses,
    CensusEquipmentStatusesEnum,
    CensusEquipmentStatusReasons,
} from "../../data/models/CensusEnums";
import {
    JSONCensusEquipmentsActionsConfirmation,
    ManualChange,
} from "../../data/models/JSONCensusEquipmentsActionsConfirmation";
import {MappedDetailCount} from "../../data/models/JSONCensusSuggestedActionsAnalysisSummary";
import {useCensusEnums} from "../../data/hooks/CecnsusCoolerControllerHooks";
import {AppTranslations} from "../../../../../components/Utils/translations/AppTranslations";
import {getHeaderDetailsHeader} from "../../pages/CensusStatusPage/utils";
import {Section} from "../../pages/CensusStatusPage/types";
import {SuggestedActionsContext} from "../../providers/SuggestedActionsProvider";

declare var AppTranslations: AppTranslations;

interface EquipmentWithStatusSectionProps {
    equipmentCount: number;
    tableHeaderName: string;
    status: CensusEquipmentStatusesEnum;
    statusDetail?: CensusEquipmentStatusDetailsEnum;
    inventoryStatusReason?: CensusEquipmentStatusReasons;
    detailCount?: MappedDetailCount[];
    translatedActionsEnum: Actions;
    suggestedAction?: ActionsEnum;
    addOrRemoveAndMergeManualChange: (
        action: JSONCensusEquipmentsActionsConfirmation,
        manualChange: ManualChange,
    ) => void;
    level: number;
    height?: string;
    divRef?: React.RefObject<HTMLDivElement>;
    index?: number;
    toggleSection?: (section: Section) => void;
    availableActions: ActionsEnum[],
    descriptions: CensusEquipmentStatuses & CensusEquipmentStatusDetails & CensusEquipmentStatusReasons
}

export const EquipmentWithStatusSection: React.FC<
    EquipmentWithStatusSectionProps
> = ({
         equipmentCount,
         tableHeaderName,
         status,
         statusDetail,
         inventoryStatusReason,
         detailCount,
         translatedActionsEnum,
         suggestedAction,
         level,
         height,
         divRef,
         index,
         toggleSection,
         availableActions,
         descriptions,
         ...rest
     }) => {
    const theme = useTheme();
    const {data, isLoading, error} = useCensusEnums(true);

    const ref = React.useRef<HTMLDivElement>(null);

    const onClick = (level: number) => () => {
        toggleSection({name: tableHeaderName, level: level});
    };

    const getDescriptions = () => {
        if (status && !statusDetail && !inventoryStatusReason) {
            return descriptions[status];
        }
        if (statusDetail && level === 0) {
            return `${descriptions[statusDetail]} 
                ${inventoryStatusReason
                ? descriptions[inventoryStatusReason]
                : ""}`;
        }
        if (level === 1) {
            return `${inventoryStatusReason && inventoryStatusReason !== CensusEquipmentStatusReasons.OTHER
                ? descriptions[inventoryStatusReason]
                : AppTranslations.massiveActions.censusCooler.otherDescription}`;
        }
        return ""
    }

    const {
        getActionByStatusAndStatusDetailsAndReason,
        mergeConfirmedActions,
        confirmedActions,
        isMixedActionSelected,
        containsEditedActions
    } = React.useContext(SuggestedActionsContext);

    const renderChildren = () => {
        if (detailCount?.length > 0) {
            return (
                <DesAccordion enableSingleSectionOpen={true}>
                    {detailCount.map((detail, index) => {
                        return (
                            <EquipmentWithStatusSection
                                key={index}
                                equipmentCount={detail.count}
                                tableHeaderName={getHeaderDetailsHeader(detail, data)}
                                inventoryStatusReason={detail?.inventoryStatusReason}
                                status={status}
                                statusDetail={statusDetail}
                                suggestedAction={detail?.suggestedAction}
                                level={1}
                                height={`calc(${height} - 50px)`}
                                toggleSection={toggleSection}
                                availableActions={detail?.availableActions}
                                translatedActionsEnum={translatedActionsEnum}
                                descriptions={descriptions}
                                {...rest}
                            />
                        );
                    })}
                </DesAccordion>
            );
        } else {
            const confirmedAction = getActionByStatusAndStatusDetailsAndReason(
                status,
                statusDetail,
                inventoryStatusReason
            );
            return (
                <SuggestedActionsTable
                    confirmedAction={confirmedAction}
                    status={status}
                    statusDetail={statusDetail}
                    statusReason={inventoryStatusReason}
                    translatedActionsEnum={translatedActionsEnum}
                    isMixedActionSelected={isMixedActionSelected(confirmedAction)}
                    height={`calc(${height} - 162px)`}
                    availableActions={availableActions}
                    {...rest}
                />
            );
        }
    };

    const accordionChildren = React.useMemo(
        () => renderChildren(),
        [
            detailCount,
            inventoryStatusReason,
            status,
            statusDetail,
            mergeConfirmedActions,
            confirmedActions,
            data,
            translatedActionsEnum
        ],
    );

    const confirmedAction = getActionByStatusAndStatusDetailsAndReason(
        status,
        statusDetail,
        inventoryStatusReason
    );

    return (
        <DesAccordionSection
            key={tableHeaderName}
            title={tableHeaderName + " (" + equipmentCount + ")"}
            id={tableHeaderName}
            keepSectionContentInDOM={false}
            styles={{
                ...accordionStyle(
                    theme as Theme,
                    (detailCount ?? [])?.length > 0,
                    level,
                    `calc(${height} - 162px)`,
                ),
            }}
            renderHeader={renderHeader(
                status,
                tableHeaderName,
                equipmentCount,
                suggestedAction,
                translatedActionsEnum,
                level,
                isMixedActionSelected(confirmedAction),
                mergeConfirmedActions,
                status,
                onClick(level),
                availableActions,
                getDescriptions(),
                statusDetail,
                confirmedAction,
                detailCount,
                containsEditedActions(status, statusDetail, inventoryStatusReason)
            )}
            containerRef={ref}
        >
            {accordionChildren}
        </DesAccordionSection>
    );
};
